import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Hook to automatically track page/route changes
 * This provides basic route tracking functionality without external dependencies
 */
export function useRouteTracking() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    // Simple route tracking for debugging and development
    console.log('🔗 Route changed:', {
      path: location.pathname,
      search: location.search,
      authenticated: isAuthenticated,
      userTier: user?.user_metadata?.tier || 'free'
    });
  }, [location, user, isAuthenticated]);
}

/**
 * Hook for tracking specific named pages with custom properties
 * Use this in components that want to override the automatic route tracking
 */
export function usePageTracking(pageName: string, properties?: Record<string, any>) {
  useEffect(() => {
    console.log('📄 Page tracked:', pageName, properties);
  }, [pageName, properties]);
}

/**
 * Hook for tracking events
 * Provides a convenient way to track user interactions
 */
export function useEventTracking() {
  const track = (eventName: string, properties?: Record<string, any>) => {
    console.log('📊 Event tracked:', eventName, {
      tracked_at: new Date().toISOString(),
      url: window.location.href,
      path: window.location.pathname,
      ...properties
    });
  };

  return { track, isReady: true };
}